import React, { useEffect, useRef, useState } from "react";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import {
  createReferral,
  createReferralDraft,
  submitReferralDraft,
  uploadReferralDocument,
  dispatchReferralFax,
} from "../APIs/referralApi";
import { getSpecialties } from "../APIs/dropdownApi";
import { getActivePatients } from "../APIs/activePatientApi";
import { searchProviders, getProviderByNpi } from "../APIs/providerApi";
import { getPatient, getPatientPreference } from "../APIs/patientService";

const URGENCY_OPTIONS = [
  { value: "ROUTINE", label: "Routine" },
  { value: "WITHIN_30_DAYS", label: "Within 30 Days" },
  { value: "WITHIN_7_DAYS", label: "Within 7 Days" },
  { value: "WITHIN_3_DAYS", label: "Within 3 Days" },
];

const REASON_CHIPS = [
  "Follow-up",
  "New Symptoms",
  "Second Opinion",
  "Abnormal Results",
  "Other",
];

const DELIVERY_OPTIONS = [
  { value: "IN_APP", label: "Send via MedMaze" },
  { value: "FAX", label: "Send via Fax" },
  { value: "BOTH", label: "Send via Both" },
];

const DOC_CATEGORIES = [
  { value: "REFERRAL_DOCUMENT", label: "Referral Document" },
  { value: "CLINICAL_NOTES", label: "Clinical Notes" },
  { value: "LAB_RESULTS", label: "Lab Results" },
  { value: "IMAGING", label: "Imaging" },
];

/*
const ROUTING_OPTIONS = [
  { value: "PROVIDER_DIRECTED", label: "Provider Directed", desc: "I already know which specialist to send this to" },
  { value: "SMART", label: "SMART", desc: "System selects best-matched specialist automatically" },
  { value: "PATIENT_CHOICE", label: "Patient Choice", desc: "Let the patient choose from a shortlist of matched specialists" },
];
*/

const MAX_DOCS = 5;
const MAX_DOC_BYTES = 50 * 1024 * 1024;
const ALLOWED_DOC_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/tiff",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/csv",
  "application/csv",
]);
const ALLOWED_DOC_LABEL = "PDF, JPEG, PNG, TIFF, DOCX, DOC, CSV";

const STEP_TITLES = [
  "Find a Specialist",
  "Send Referral",
  "Clinical Details",
  "Delivery Method",
];

export default function ReferralIntakeModal({
  providerId,
  onClose,
  onSubmitted,
}) {
  const [step, setStep] = useState(1);
  const [specialties, setSpecialties] = useState([]);
  const [allPatients, setAllPatients] = useState([]);
  const [patientQuery, setPatientQuery] = useState("");
  const [specialistQuery, setSpecialistQuery] = useState("");
  const [specialistResults, setSpecialistResults] = useState([]);
  const [specialistSearching, setSpecialistSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [documents, setDocuments] = useState([]);
  const [draftReferralId, setDraftReferralId] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState("IN_APP");
  const [faxNumber, setFaxNumber] = useState("");
  const [showReview, setShowReview] = useState(false);
  const [patientPrefsOpen, setPatientPrefsOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showRequiredHint, setShowRequiredHint] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    patient: null,
    specialty: "",
    urgency: "ROUTINE",
    reasonChips: [],
    additionalNotes: "",
    city: "",
    state: "",
    insurancePlan: "",
    targetProvider: null,
  });
  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const isExternal = form.targetProvider?._type === "external";
  const isFax = deliveryMethod === "FAX";
  const isBoth = deliveryMethod === "BOTH";
  const needsFax = isExternal || isFax || isBoth;

  useEffect(() => {
    getSpecialties()
      .then(setSpecialties)
      .catch(() => {});
    getActivePatients(providerId, { size: 200 })
      .then((data) =>
        setAllPatients(data?.content ?? (Array.isArray(data) ? data : [])),
      )
      .catch(() => {});
  }, [providerId]);

  useEffect(() => {
    if (!form.patient) return;
    const pid = form.patient.patientId;
    getPatient(pid)
      .then((p) =>
        setForm((f) => ({ ...f, city: p.city ?? "", state: p.state ?? "" })),
      )
      .catch(() => {});
    getPatientPreference(pid)
      .then((prefs) => {
        if (!prefs?.planNames) return;
        const plan = Array.isArray(prefs.planNames)
          ? prefs.planNames.join(", ")
          : String(prefs.planNames);
        setForm((f) => ({ ...f, insurancePlan: plan }));
      })
      .catch(() => {});
  }, [form.patient]);

  useEffect(() => {
    if (!form.targetProvider) return;
    if (form.targetProvider._type === "external") {
      setDeliveryMethod("FAX");
      setFaxNumber(form.targetProvider.faxNumber ?? "");
    } else {
      setDeliveryMethod("IN_APP");
      setFaxNumber("");
    }
  }, [form.targetProvider]);

  useEffect(() => {
    if (step !== 1 || specialistQuery.length < 2) {
      setSpecialistResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSpecialistSearching(true);
      const digits = specialistQuery.replace(/\D/g, "");
      try {
        if (digits.length === 10) {
          const preview = await getProviderByNpi(digits);
          setSpecialistResults([
            { ...preview, npi: digits, _type: "external" },
          ]);
        } else {
          const list = await searchProviders(specialistQuery, 10);
          setSpecialistResults(list.map((p) => ({ ...p, _type: "internal" })));
        }
      } catch {
        setSpecialistResults([]);
      } finally {
        setSpecialistSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [specialistQuery, step]);

  const patientResults =
    patientQuery.trim().length < 1
      ? []
      : allPatients
          .filter((p) => {
            const q = patientQuery.toLowerCase();
            return (
              `${p.firstName ?? ""} ${p.lastName ?? ""}`
                .toLowerCase()
                .includes(q) || String(p.patientId).includes(q)
            );
          })
          .slice(0, 10);

  const canProceedToReview =
    !!form.patient &&
    !!form.specialty &&
    !!form.targetProvider &&
    (!needsFax || faxNumber.trim().length > 0) &&
    documents.every((d) => !d.error);

  const handleNext = () => {
    if (step === 2 && (!form.patient || !form.specialty)) {
      setShowRequiredHint(true);
      return;
    }
    setShowRequiredHint(false);
    setStep((s) => Math.min(s + 1, 4));
  };
  const handleBack = () => setStep((s) => Math.max(s - 1, 1));

  const addFileToDocuments = (file) => {
    if (draftReferralId) return;
    if (documents.length >= MAX_DOCS) return;
    const typeErr = !ALLOWED_DOC_TYPES.has(file.type);
    const sizeErr = file.size > MAX_DOC_BYTES;
    const err = typeErr
      ? `Type not allowed. Use: ${ALLOWED_DOC_LABEL}`
      : sizeErr
        ? "Exceeds 50 MB limit"
        : "";
    setDocuments((d) => [
      ...d,
      { file, category: "REFERRAL_DOCUMENT", error: err },
    ]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    Array.from(e.dataTransfer.files).forEach(addFileToDocuments);
  };

  const handleFileInput = (e) => {
    Array.from(e.target.files).forEach(addFileToDocuments);
    e.target.value = "";
  };

  const updateDoc = (idx, changes) =>
    setDocuments((d) =>
      d.map((doc, i) => (i === idx ? { ...doc, ...changes } : doc)),
    );
  const removeDoc = (idx) => {
    if (draftReferralId) return;
    setDocuments((d) => d.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const reason = [...form.reasonChips, form.additionalNotes.trim()]
        .filter(Boolean)
        .join(" | ");
      const payload = {
        referringProviderId: providerId,
        patientId: form.patient.patientId,
        routingMode: isExternal ? "EXTERNAL" : "PROVIDER_DIRECTED",
        specialty: form.specialty,
        urgency: form.urgency,
        reason,
        ...(form.state ? { state: form.state } : {}),
        ...(form.city ? { city: form.city } : {}),
        ...(form.insurancePlan ? { insurancePlan: form.insurancePlan } : {}),
        ...(!isExternal
          ? {
              referredSpecialistProviderId:
                form.targetProvider.providerId ?? form.targetProvider.id,
            }
          : {}),
        ...(isExternal ? { externalProviderNpi: form.targetProvider.npi } : {}),
        ...(needsFax ? { externalProviderFaxOverride: faxNumber.trim() } : {}),
      };
      if (!needsFax) {
        let referralId = draftReferralId;
        if (!referralId) {
          const draft = await createReferralDraft({
            ...payload,
            expectedDocumentCount: documents.length,
          });
          referralId = draft.referralId;
          setDraftReferralId(referralId);
        }
        for (let index = 0; index < documents.length; index += 1) {
          const doc = documents[index];
          if (doc.file && !doc.error && !doc.uploaded) {
            await uploadReferralDocument(
              referralId,
              doc.file,
              doc.category,
              providerId,
            );
            updateDoc(index, { uploaded: true });
          }
        }
        await submitReferralDraft(referralId);
      } else {
        const referral = await createReferral(payload);
        for (const doc of documents) {
          if (doc.file && !doc.error) {
            await uploadReferralDocument(
              referral.referralId,
              doc.file,
              doc.category,
              providerId,
            );
          }
        }
        await dispatchReferralFax(referral.referralId);
      }
      onSubmitted?.();
      onClose();
    } catch (err) {
      const fields = err?.response?.data?.fields;
      setError(
        (fields && Object.entries(fields).map(([field, message]) => `${field}: ${message}`).join("; ")) ||
          err?.response?.data?.message ||
          err.message ||
          "Submission failed",
      );
      setShowReview(false);
    } finally {
      setSubmitting(false);
    }
  };

  const urgencyLabel =
    URGENCY_OPTIONS.find((u) => u.value === form.urgency)?.label ??
    form.urgency;
  const deliveryLabel = isBoth
    ? `Both · ${faxNumber}`
    : isFax
      ? `Fax · ${faxNumber}`
      : "MedMaze (In App)";

  return (
    <>
      {/* Main modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b">
            <div>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
                Step {step} of 4
              </p>
              <h2 className="text-lg font-semibold">{STEP_TITLES[step - 1]}</h2>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <X size={20} />
            </button>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-gray-100">
            <div
              className="h-1 bg-green-500 transition-all duration-300"
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>

          <div className="p-5 space-y-5">
            {/* ─── STEP 1: Find a Specialist ─── */}
            {step === 1 && (
              <>
                <p className="text-sm text-gray-500">
                  Search by name or enter a 10-digit NPI to find a specialist.
                </p>

                {form.targetProvider ? (
                  <div className="border rounded-lg p-4 bg-green-50 space-y-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold">
                            {form.targetProvider.firstName}{" "}
                            {form.targetProvider.lastName}
                            {form.targetProvider.credential && (
                              <span className="text-gray-500 font-normal">
                                , {form.targetProvider.credential}
                              </span>
                            )}
                          </p>
                          {isExternal ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium border border-green-200">
                              External
                            </span>
                          ) : (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium border border-blue-200">
                              MedMaze
                            </span>
                          )}
                        </div>
                        {form.targetProvider.specialty && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {form.targetProvider.specialty}
                          </p>
                        )}
                        {form.targetProvider.locations?.[0] && (
                          <p className="text-xs text-gray-400">
                            {form.targetProvider.locations[0].city},{" "}
                            {form.targetProvider.locations[0].state}
                          </p>
                        )}
                        {isExternal && (
                          <p className="text-xs mt-1">
                            {form.targetProvider.faxNumber ? (
                              <span className="text-gray-600">
                                Fax: {form.targetProvider.faxNumber}
                              </span>
                            ) : (
                              <span className="text-amber-600">
                                No fax on file — you can enter one in Step 4
                              </span>
                            )}
                          </p>
                        )}
                        {!isExternal && form.targetProvider.npi && (
                          <p className="text-xs text-gray-400">
                            NPI: {form.targetProvider.npi}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          set("targetProvider", null);
                          setSpecialistQuery("");
                          setSpecialistResults([]);
                        }}
                        className="text-xs text-red-500 hover:underline shrink-0 ml-2"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <input
                      className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                      placeholder="Search by name or enter NPI..."
                      value={specialistQuery}
                      onChange={(e) => setSpecialistQuery(e.target.value)}
                      autoFocus
                    />
                    {specialistSearching && (
                      <p className="text-xs text-gray-400">Searching...</p>
                    )}
                    {!specialistSearching && specialistResults.length > 0 && (
                      <div className="border rounded-lg mt-1 max-h-60 overflow-y-auto shadow-sm">
                        {specialistResults.map((p, i) => (
                          <button
                            key={p.providerId ?? p.npi ?? i}
                            className="w-full text-left px-3 py-3 text-sm hover:bg-blue-50 border-b last:border-b-0 transition"
                            onClick={() => {
                              set("targetProvider", p);
                              setSpecialistQuery("");
                              setSpecialistResults([]);
                            }}
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">
                                {p.firstName} {p.lastName}
                                {p.credential && (
                                  <span className="text-gray-500 font-normal">
                                    , {p.credential}
                                  </span>
                                )}
                              </span>
                              {p._type === "external" ? (
                                <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                                  External
                                </span>
                              ) : (
                                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                                  MedMaze
                                </span>
                              )}
                            </div>
                            {p.specialty && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                {p.specialty}
                              </p>
                            )}
                            {p.locations?.[0] && (
                              <p className="text-xs text-gray-400">
                                {p.locations[0].city}, {p.locations[0].state}
                                {p._type === "external" && p.faxNumber && (
                                  <span className="ml-2">
                                    · Fax: {p.faxNumber}
                                  </span>
                                )}
                              </p>
                            )}
                            {p._type === "internal" && p.npi && (
                              <p className="text-xs text-gray-400">
                                NPI: {p.npi}
                              </p>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {!specialistSearching &&
                      specialistQuery.length >= 2 &&
                      specialistResults.length === 0 && (
                        <p className="text-xs text-gray-400">
                          No providers found.
                        </p>
                      )}
                  </>
                )}
              </>
            )}

            {/* ─── STEP 2: Send Referral ─── */}
            {step === 2 && (
              <div className="space-y-5">
                {/* Patient */}
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Patient <span className="text-red-500">*</span>
                  </label>
                  {form.patient ? (
                    <div className="flex items-center justify-between border rounded-lg p-3 bg-green-50">
                      <span className="text-sm font-medium">
                        {form.patient.firstName} {form.patient.lastName}
                      </span>
                      <button
                        onClick={() => {
                          set("patient", null);
                          setPatientQuery("");
                        }}
                        className="text-xs text-red-500 hover:underline"
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 ${
                          showRequiredHint && !form.patient
                            ? "border-red-400"
                            : ""
                        }`}
                        placeholder="Search patient by name or ID..."
                        value={patientQuery}
                        onChange={(e) => setPatientQuery(e.target.value)}
                      />
                      {showRequiredHint && !form.patient && (
                        <p className="text-xs text-red-500 mt-1">
                          Required field — please complete
                        </p>
                      )}
                      {patientResults.length > 0 && (
                        <div className="border rounded-lg mt-1 max-h-40 overflow-y-auto shadow-sm">
                          {patientResults.map((p) => (
                            <button
                              key={p.patientId}
                              className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 border-b last:border-b-0"
                              onClick={() => {
                                set("patient", p);
                                setPatientQuery("");
                              }}
                            >
                              <span className="font-medium">
                                {p.firstName} {p.lastName}
                              </span>
                              {p.dateOfBirth && (
                                <span className="text-gray-400 ml-2 text-xs">
                                  DOB: {p.dateOfBirth}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Specialty */}
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Specialty <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 ${
                      showRequiredHint && !form.specialty
                        ? "border-red-400"
                        : ""
                    }`}
                    value={form.specialty}
                    onChange={(e) => set("specialty", e.target.value)}
                  >
                    <option value="">Select specialty...</option>
                    {specialties.map((s) => (
                      <option key={s.id} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  {showRequiredHint && !form.specialty && (
                    <p className="text-xs text-red-500 mt-1">
                      Required field — please complete
                    </p>
                  )}
                </div>

                {/* Urgency */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    How soon does the patient need care?
                  </label>
                  <div className="space-y-2">
                    {URGENCY_OPTIONS.map((u) => (
                      <label
                        key={u.value}
                        className={`flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition ${
                          form.urgency === u.value
                            ? "border-green-500 bg-green-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          className="accent-green-600"
                          checked={form.urgency === u.value}
                          onChange={() => set("urgency", u.value)}
                        />
                        <span className="text-sm font-medium">{u.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ─── STEP 3: Clinical Details ─── */}
            {step === 3 && (
              <div className="space-y-5">
                {/* Reason chips */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Reason for Referral{" "}
                    <span className="text-gray-400 font-normal text-xs">
                      (optional)
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {REASON_CHIPS.map((chip) => {
                      const active = form.reasonChips.includes(chip);
                      return (
                        <button
                          key={chip}
                          onClick={() =>
                            set(
                              "reasonChips",
                              active
                                ? form.reasonChips.filter((c) => c !== chip)
                                : [...form.reasonChips, chip],
                            )
                          }
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                            active
                              ? "bg-green-600 text-white border-green-600"
                              : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                          }`}
                        >
                          {chip}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Additional Notes */}
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Additional Notes{" "}
                    <span className="text-gray-400 font-normal text-xs">
                      (optional)
                    </span>
                  </label>
                  <textarea
                    className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none"
                    rows={3}
                    placeholder="Any relevant context for the specialist..."
                    value={form.additionalNotes}
                    onChange={(e) => set("additionalNotes", e.target.value)}
                  />
                </div>

                {/* Drag & Drop Documents */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Upload Supporting Documents{" "}
                    <span className="text-gray-400 font-normal text-xs">
                      (optional)
                    </span>
                  </label>

                  {documents.length < MAX_DOCS ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition select-none ${
                        dragOver
                          ? "border-green-400 bg-green-50"
                          : "border-gray-300 hover:border-gray-400 bg-gray-50"
                      }`}
                    >
                      <p className="text-sm font-medium text-gray-600">
                        Drag & drop files here, or{" "}
                        <span className="text-green-600 underline">Browse</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {ALLOWED_DOC_LABEL}
                      </p>
                      <p className="text-xs text-gray-400">
                        Max {MAX_DOCS} files · 50 MB each
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-amber-600 text-center border border-amber-200 rounded-lg py-3 bg-amber-50">
                      Maximum {MAX_DOCS} files reached.
                    </p>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif,.docx,.doc,.csv"
                    className="hidden"
                    onChange={handleFileInput}
                  />

                  {documents.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {documents.map((doc, idx) => (
                        <div key={idx} className="space-y-1">
                          <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-white">
                            <span className="flex-1 text-xs text-gray-700 truncate">
                              {doc.file.name}
                            </span>
                            <select
                              className="border-0 text-xs text-gray-600 focus:outline-none bg-transparent shrink-0"
                              value={doc.category}
                              onChange={(e) =>
                                updateDoc(idx, { category: e.target.value })
                              }
                            >
                              {DOC_CATEGORIES.map((c) => (
                                <option key={c.value} value={c.value}>
                                  {c.label}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => removeDoc(idx)}
                              className="text-gray-400 hover:text-red-500 shrink-0 ml-1"
                            >
                              <X size={14} />
                            </button>
                          </div>
                          {doc.error && (
                            <p className="text-xs text-red-600 px-1">
                              {doc.error}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ─── STEP 4: Delivery Method ─── */}
            {step === 4 && (
              <div className="space-y-5">
                {/* Delivery options */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    How would you like to send this referral?
                  </label>

                  {isExternal ? (
                    <div className="flex items-center gap-2 border rounded-lg px-4 py-3 bg-gray-50">
                      <span className="text-sm font-medium text-gray-700">
                        Send via Fax
                      </span>
                      <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">
                        Locked — external provider
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {DELIVERY_OPTIONS.map((opt) => (
                        <label
                          key={opt.value}
                          className={`flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition ${
                            deliveryMethod === opt.value
                              ? "border-green-500 bg-green-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            className="accent-green-600"
                            checked={deliveryMethod === opt.value}
                            onChange={() => {
                              setDeliveryMethod(opt.value);
                              if (opt.value === "IN_APP") setFaxNumber("");
                            }}
                          />
                          <span className="text-sm font-medium">
                            {opt.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Fax number */}
                {needsFax && (
                  <div>
                    <label className="block text-sm font-semibold mb-1">
                      Fax Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                      placeholder="(555) 555-5555"
                      value={faxNumber}
                      onChange={(e) => setFaxNumber(e.target.value)}
                    />
                  </div>
                )}

                {/* Patient Preferences — collapsible */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition"
                    onClick={() => setPatientPrefsOpen((o) => !o)}
                  >
                    <span>
                      Patient Preferences{" "}
                      <span className="text-gray-400 font-normal">
                        (optional)
                      </span>
                    </span>
                    {patientPrefsOpen ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </button>
                  {patientPrefsOpen && (
                    <div className="p-4 space-y-4 border-t">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            City
                          </label>
                          <input
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                            placeholder="City..."
                            value={form.city}
                            onChange={(e) => set("city", e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            State
                          </label>
                          <input
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                            placeholder="State..."
                            value={form.state}
                            onChange={(e) => set("state", e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insurance Plan
                        </label>
                        <input
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                          placeholder="Insurance plan name..."
                          value={form.insurancePlan}
                          onChange={(e) => set("insurancePlan", e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {error && <p className="text-red-600 text-sm">{error}</p>}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center px-5 py-4 border-t bg-gray-50 sticky bottom-0">
            <button
              disabled={step === 1}
              onClick={handleBack}
              className="px-4 py-2 text-sm rounded-lg border disabled:opacity-40 hover:bg-gray-100 transition"
            >
              Back
            </button>
            {step < 4 ? (
              <button
                disabled={step === 1 && !form.targetProvider}
                onClick={handleNext}
                className="px-5 py-2 text-sm rounded-lg bg-green-600 text-white disabled:opacity-40 hover:bg-green-700 transition"
              >
                Next
              </button>
            ) : (
              <button
                disabled={!canProceedToReview}
                onClick={() => setShowReview(true)}
                className="px-5 py-2 text-sm rounded-lg bg-green-600 text-white disabled:opacity-40 hover:bg-green-700 transition"
              >
                Review
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Review Overlay */}
      {showReview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-semibold">Review Referral</h2>
              <button
                onClick={() => setShowReview(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-2.5 text-sm">
              <ReviewRow
                label="Specialist"
                value={
                  form.targetProvider
                    ? `${form.targetProvider.firstName ?? ""} ${form.targetProvider.lastName ?? ""}${
                        form.targetProvider.credential
                          ? `, ${form.targetProvider.credential}`
                          : ""
                      } (${isExternal ? "External" : "MedMaze"})`
                    : "—"
                }
              />
              {isExternal && <ReviewRow label="Fax" value={faxNumber || "—"} />}
              <ReviewRow
                label="Patient"
                value={
                  form.patient
                    ? `${form.patient.firstName} ${form.patient.lastName}`
                    : "—"
                }
              />
              <ReviewRow label="Specialty" value={form.specialty || "—"} />
              <ReviewRow label="Urgency" value={urgencyLabel} />
              <ReviewRow
                label="Reason"
                value={
                  [...form.reasonChips, form.additionalNotes.trim()]
                    .filter(Boolean)
                    .join(" · ") || "—"
                }
              />
              <ReviewRow label="Delivery" value={deliveryLabel} />
              {(form.city || form.state) && (
                <ReviewRow
                  label="Location"
                  value={[form.city, form.state].filter(Boolean).join(", ")}
                />
              )}
              {form.insurancePlan && (
                <ReviewRow label="Insurance" value={form.insurancePlan} />
              )}
              <div className="pt-1">
                <span className="font-medium text-gray-500">Documents: </span>
                {documents.filter((d) => d.file && !d.error).length === 0 ? (
                  <span className="text-gray-400">None</span>
                ) : (
                  <ul className="mt-1 space-y-0.5 pl-2">
                    {documents
                      .filter((d) => d.file && !d.error)
                      .map((d, i) => (
                        <li key={i} className="text-gray-700">
                          {d.file.name}{" "}
                          <span className="text-gray-400 text-xs">
                            (
                            {
                              DOC_CATEGORIES.find((c) => c.value === d.category)
                                ?.label
                            }
                            )
                          </span>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            </div>

            {error && <p className="px-5 pb-2 text-red-600 text-sm">{error}</p>}

            <div className="flex justify-between items-center px-5 py-4 border-t bg-gray-50">
              <button
                onClick={() => setShowReview(false)}
                className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-100 transition"
              >
                Back
              </button>
              <button
                disabled={submitting}
                onClick={handleSubmit}
                className="px-5 py-2 text-sm rounded-lg bg-green-600 text-white disabled:opacity-40 hover:bg-green-700 transition"
              >
                {submitting ? "Submitting..." : "Submit Referral"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="font-medium text-gray-500 w-24 shrink-0">{label}:</span>
      <span className="text-gray-800">{value || "—"}</span>
    </div>
  );
}
